import type { FeatureFlag } from '@/types/control/feature-flag';

import { controlApiRequest } from './control-api-client';

/**
 * Platform feature flags, proxied by control-api from mesh.
 *
 * **Use `/api/admin/feature-flags`, never `/api/admin/flags`.** The latter is a
 * second, duplicate proxy on its own `admin`-gated group, registered only when
 * Control's mesh client is configured, with no caller anywhere — T13 deletes
 * it. Worse, calling it while unregistered would not 404: the router's static
 * fallback serves the SPA's `index.html` with a `200`, so the request would
 * parse as an empty body and the page would render "no flags" with no error at
 * all. These routes, by contrast, are registered unconditionally on the
 * superuser group and each handler nil-checks the mesh client itself.
 */

const BASE = '/api/admin/feature-flags';

type FlagResponse = {
  flag_name?: unknown;
  enabled_tenant_ids?: unknown;
  global_enabled?: unknown;
};

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function toDomain(res: FlagResponse): FeatureFlag {
  return {
    flagName: asString(res.flag_name),
    enabledTenantIds: Array.isArray(res.enabled_tenant_ids)
      ? (res.enabled_tenant_ids as unknown[]).map(asString).filter(Boolean)
      : [],
    globalEnabled: res.global_enabled === true,
  };
}

export const featureFlagsService = {
  /**
   * Returns every known flag, including ones never written — mesh substitutes
   * an empty state for those. Not tenant-scoped: `tenant_id` is not a parameter
   * of this route, so one read serves every tenant selection.
   */
  async list(signal?: AbortSignal): Promise<FeatureFlag[]> {
    const res = await controlApiRequest<FlagResponse[] | null>({
      path: BASE,
      signal,
    });

    // Control proxies mesh's bytes verbatim, so anything mesh changes reaches
    // the browser unmediated — guard even though the handler is well behaved.
    if (!Array.isArray(res)) {
      return [];
    }

    return res.map(toDomain);
  },

  /** 204 with no body, so the caller has to re-read to see the new state. */
  async setForTenant(
    tenantId: string,
    flagName: string,
    enabled: boolean
  ): Promise<void> {
    await controlApiRequest<void>({
      method: enabled ? 'POST' : 'DELETE',
      path: `/api/admin/tenants/${encodeURIComponent(tenantId)}/feature-flags/${encodeURIComponent(flagName)}`,
    });
  },
};
